import useBookingStore, { BookingMethod } from "@/store/booking-store";
import { formatDateWithLocale } from "@/utils/format-date-with-locale";
import { Card } from "@heroui/card";
import { Button, Divider, Input } from "@heroui/react";
import {
  IconCalendar,
  IconDiscount2,
  IconSquareX,
  IconTicket,
} from "@tabler/icons-react";
import { useLocale, useTranslations } from "../../../../../i18n";
import React, { useEffect, useState } from "react";
import { CancelDialog } from "./cancel-dialog";
import PaymentButton from "./payment-button";
import { useAuthStore } from "@/store/auth-store";
import { useStudioStore } from "@/store/studio-store";
import type { UserPassWithDetails } from "@/types/UserPass";
import type { Pass } from "@/types/Pass";
import { api } from "@/lib/api";
import toast from "react-hot-toast";

export interface BookingResult {
  id: string;
  event: {
    name: string;
    startTime: Date;
    endTime: Date;
    room?: string | null;
    teacher?: {
      name: string;
      avatar?: string;
    } | null;
  };
  subscriptionUsed: {
    name: string;
    type: "membership" | "pass";
    creditsRemaining: number | null;
    classesRemaining?: number;
  };
}

// Helper function to get localized text
const getLocalizedText = (
  multilingualData: any,
  locale: string,
  fallback: string | null,
  defaultLocale: string = "en",
): string => {
  if (typeof multilingualData === "string") {
    return multilingualData;
  }

  // If no multilingual data, return fallback
  if (!multilingualData || typeof multilingualData !== "object") {
    return fallback || "";
  }

  // Try to get text in current locale
  if (multilingualData[locale]) {
    return multilingualData[locale];
  }

  // Try default locale
  if (multilingualData[defaultLocale]) {
    return multilingualData[defaultLocale];
  }

  // Try first available language
  const availableLanguages = Object.keys(multilingualData);
  if (
    availableLanguages.length > 0 &&
    multilingualData[availableLanguages[0]]
  ) {
    return multilingualData[availableLanguages[0]];
  }

  // Final fallback
  return fallback || "";
};

function Summary({
  pay,
  isLoading,
  isBookingValidHook,
  buttonLabel,
}: {
  pay: () => Promise<void>;
  isLoading: boolean;
  isBookingValidHook: () => boolean;
  buttonLabel: () => string;
}) {
  const studio = useStudioStore((state) => state.studio);
  const locale = useLocale();
  const t = useTranslations("checkout");

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [promoInput, setPromoInput] = useState("");

  const { user } = useAuthStore();

  const {
    selectedEvent,
    selectedPass,
    selectedUserPass,
    bookingMethod,
    participants,
    setParticipants,
    // addParticipant,
    discountInfo,
    setDiscountInfo,
    isApplyingDiscount,
    setIsApplyingDiscount,
    discountError,
    setDiscountError,
    clearDiscount,
  } = useBookingStore();

  useEffect(() => {
    setParticipants([
      {
        id: user?.id,
        userId: user?.id,
        name: user?.name || "",
        email: user?.email || "",
        eventId: selectedEvent?.id,
        creditsUsed: 1,
        studioId: studio?.id || "",
      },
    ]);
  }, [user]);

  // Helper functions to get localized names
  const getLocalizedClassName = (): string => {
    if (selectedEvent?.title) {
      return getLocalizedText(
        selectedEvent.title,
        locale,
        null,
        studio?.defaultLocale || "en",
      );
    }
    return t("classFallback");
  };

  const getLocalizedPassName = (pass: UserPassWithDetails | Pass): string => {
    return getLocalizedText(
      pass?.title,
      locale,
      pass?.title,
      studio?.defaultLocale || "en",
    );
  };

  // Helper function to get non-first-time participants count
  const getNonFirstTimeParticipantsCount = () => {
    // Count non-first-time participants + the logged-in user (unless they're marked as first-time)
    const guestNonFirstTime = participants.filter(
      (p) => !p.isFirstTime && p.id !== user?.id,
    ).length;
    const userIsFirstTime =
      participants.find((p) => p.id === user?.id)?.isFirstTime || false;

    return guestNonFirstTime + (userIsFirstTime ? 0 : 1);
  };

  // Helper function to get total participants count
  const getTotalParticipantsCount = () => {
    // Always include the logged-in user + additional participants
    return Math.max(participants.length, 1); // Ensure at least 1 (the user)
  };

  // Validation function to check if booking is valid
  const isBookingValid = () => {
    const nonFirstTimeParticipants = getNonFirstTimeParticipantsCount();

    if (bookingMethod === BookingMethod.USER_PASS && selectedUserPass) {
      // For unlimited passes, only one person can use it
      if (
        selectedUserPass.creditsInitial === null &&
        nonFirstTimeParticipants > 1
      ) {
        return false;
      }

      // For limited passes, check if enough credits
      if (
        selectedUserPass.creditsRemaining !== null &&
        selectedUserPass.creditsRemaining < nonFirstTimeParticipants
      ) {
        return false;
      }
    }

    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass) {
      // For unlimited passes, only one person can use it
      if (selectedPass.credits === null && nonFirstTimeParticipants > 1) {
        return false;
      }

      // For limited passes (except single class), check if enough credits
      if (
        selectedPass.credits !== null &&
        selectedPass.credits !== 1 &&
        selectedPass.credits < nonFirstTimeParticipants
      ) {
        return false;
      }
    }

    return true;
  };

  const getPassPrice = () => {
    const currency = studio?.currency || "CHF";
    const totalParticipants = getTotalParticipantsCount();

    if (bookingMethod === BookingMethod.DROP_IN) {
      const dropInPrice = selectedEvent?.dropInPrice || 0;

      return (dropInPrice * totalParticipants).toFixed(2) + " " + currency;
    }

    if (bookingMethod === BookingMethod.USER_PASS) {
      if (!selectedUserPass) return "";

      return selectedUserPass?.creditsInitial === null
        ? t("unlimited")
        : t("creditsRemaining", { count: selectedUserPass.creditsRemaining! });
    }

    if (bookingMethod === BookingMethod.NEW_PASS) {
      if (selectedPass?.credits === 1) {
        // Single class pass - each participant needs their own
        return (
          (selectedPass.price * totalParticipants).toFixed(2) + " " + currency
        );
      }
      return selectedPass?.price + " " + currency;
    }
  };

  const getOverall = () => {
    const currency = studio?.currency || "CHF";

    // const totalParticipants = getTotalParticipantsCount();
    const billableParticipants = getNonFirstTimeParticipantsCount();

    // ---------------------------------------------------------------------------
    // DROP-IN
    // ---------------------------------------------------------------------------
    if (bookingMethod === BookingMethod.DROP_IN) {
      const dropInPrice = selectedEvent?.dropInPrice || 0;

      // ✅ Only bill non-first-timers
      return (dropInPrice * billableParticipants).toFixed(2) + " " + currency;
    }

    // ---------------------------------------------------------------------------
    // EXISTING USER PASS
    // ---------------------------------------------------------------------------
    if (bookingMethod === BookingMethod.USER_PASS && selectedUserPass) {
      // Unlimited pass
      if (
        selectedUserPass.creditsInitial === null &&
        billableParticipants > 1
      ) {
        return (
          t("unlimitedPassSingleUser") ||
          "Impossible de réserver pour plusieurs participants avec un seul abonnement illimité"
        );
      }

      if (
        selectedUserPass.creditsInitial === null ||
        selectedUserPass.creditsRemaining === null
      ) {
        return t("unlimited");
      }

      // Limited pass
      if (selectedUserPass.creditsRemaining < billableParticipants) {
        return (
          t("insufficientCredits") ||
          "Crédit insuffisant pour le nombre de participants"
        );
      }

      // ✅ Pass booking is free at checkout
      return t("free");
    }

    // ---------------------------------------------------------------------------
    // NEW PASS
    // ---------------------------------------------------------------------------
    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass?.price) {
      // Unlimited new pass
      if (selectedPass.credits === null && billableParticipants > 1) {
        return (
          t("unlimitedPassSingleUser") ||
          "Impossible de réserver pour plusieurs participants avec un seul abonnement illimité"
        );
      }

      if (selectedPass.credits === null) {
        return selectedPass.price + " " + currency;
      }

      // Single-class pass → one per billable participant
      if (selectedPass.credits === 1) {
        return (
          (selectedPass.price * billableParticipants).toFixed(2) +
          " " +
          currency
        );
      }

      // Multi-class pass
      if (selectedPass.credits < billableParticipants) {
        return (
          t("insufficientCredits") ||
          "Crédit insuffisant pour le nombre de participants"
        );
      }

      return selectedPass.price + " " + currency;
    }

    return "0.00 " + currency;
  };

  const getOverallWithDiscount = () => {
    if (!discountInfo) return getOverall();
    const currency = studio?.currency || "CHF";
    return parseFloat(discountInfo.newAmount).toFixed(2) + " " + currency;
  };

  const getSubscriptionLabel = () => {
    if (bookingMethod === BookingMethod.USER_PASS && selectedUserPass) {
      const localizedName = getLocalizedPassName(selectedUserPass);
      return (
        <div className="flex items-center gap-2">
          <IconTicket size={18} className="text-primary" />
          <span>{localizedName}</span>
        </div>
      );
    } else if (bookingMethod === BookingMethod.NEW_PASS && selectedPass) {
      const localizedName = getLocalizedPassName(selectedPass);
      return (
        <div className="flex items-center gap-2">
          <IconTicket size={18} className="text-primary" />
          <span>{localizedName}</span>
        </div>
      );
    }
    return t("selectBookingOption");
  };

  // Function to render credit usage breakdown for existing passes
  const renderCreditUsage = () => {
    if (bookingMethod !== BookingMethod.USER_PASS || !selectedUserPass) {
      return null;
    }

    const nonFirstTimeParticipants = getNonFirstTimeParticipantsCount();
    const currentCredits = selectedUserPass.creditsRemaining || 0;
    const creditsAfter = currentCredits - nonFirstTimeParticipants;

    if (selectedUserPass.creditsRemaining === null) {
      // Unlimited pass - no need to show credit breakdown
      return null;
    }

    return (
      <div className="mt-6 mb-4">
        <Divider className="mb-4" />
        <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wide">
          {t("creditUsage")}
        </h4>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm">{t("currentCredits")}</span>
            <span className="font-medium">{currentCredits}</span>
          </div>

          <div className="flex justify-between items-center text-red-600">
            <span className="text-sm">{t("creditsUsed")}</span>
            <span className="font-medium">-{nonFirstTimeParticipants}</span>
          </div>

          <div className="flex justify-between items-center text-green-600 font-medium">
            <span className="text-sm">{t("creditsRemaining")}</span>
            <span>{creditsAfter}</span>
          </div>
        </div>
      </div>
    );
  };

  // Function to get the subtitle for new pass purchases
  const getPassSubtitle = () => {
    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass) {
      const nonFirstTimeParticipants = getNonFirstTimeParticipantsCount();

      if (
        selectedPass.credits !== null &&
        selectedPass.credits !== 1 &&
        selectedPass.credits >= nonFirstTimeParticipants
      ) {
        return `${nonFirstTimeParticipants} ${t("creditsUsedSubtitle")}`;
      }
    }

    if (bookingMethod === BookingMethod.USER_PASS && selectedUserPass) {
      return t("paidWithCredits");
    }

    return null;
  };

  /* const handleAddGuest = () => {
    addParticipant({
      id: generateId(),
      userId: user?.id,
      name: "",
      email: "", // Empty email for guests
      eventId: selectedEvent?.id,
      creditsUsed: 1,
      studioId: studio?.id || "",
      isFirstTime: false,
    });
  }; */

  const getValidationError = () => {
    const nonFirstTimeParticipants = getNonFirstTimeParticipantsCount();

    if (bookingMethod === BookingMethod.USER_PASS && selectedUserPass) {
      if (
        selectedUserPass.creditsInitial === null &&
        nonFirstTimeParticipants > 1
      ) {
        return t("unlimitedPassSingleUser");
      }
      if (
        selectedUserPass.creditsRemaining !== null &&
        selectedUserPass.creditsRemaining < nonFirstTimeParticipants
      ) {
        return t("insufficientCredits");
      }
    }

    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass) {
      if (selectedPass.credits === null && nonFirstTimeParticipants > 1) {
        return t("unlimitedPassSingleUser");
      }
      if (
        selectedPass.credits !== null &&
        selectedPass.credits !== 1 &&
        selectedPass.credits < nonFirstTimeParticipants
      ) {
        return t("insufficientCredits");
      }
    }

    return null;
  };

  /* const getPrice = (): number => {
    const totalParticipants = getTotalParticipantsCount();

    switch (bookingMethod) {
      case BookingMethod.NEW_PASS:
        if (!selectedPass) return 0;
        if (selectedPass.classes === 1) {
          return (selectedPass.price || 0) * totalParticipants;
        }
        return selectedPass.price || 0;

      case BookingMethod.DROP_IN:
        return (selectedEvent?.dropInPrice || 0) * totalParticipants;

      case BookingMethod.USER_PASS:
        return 0;

      default:
        return 0;
    }
  }; */

  /* const getItemId = () => {
    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass) {
      return selectedPass.id;
    }
    return undefined;
  }; */

  // Returns the numeric price before any discount, or null for non-monetary cases
  const getRawNumericPrice = (): number | null => {
    const billableParticipants = getNonFirstTimeParticipantsCount();

    if (bookingMethod === BookingMethod.USER_PASS) return null;

    if (bookingMethod === BookingMethod.DROP_IN) {
      const dropInPrice = selectedEvent?.dropInPrice || 0;
      return dropInPrice * billableParticipants;
    }

    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass?.price) {
      if (selectedPass.credits === null) return selectedPass.price;
      if (selectedPass.credits === 1)
        return selectedPass.price * billableParticipants;
      if (selectedPass.credits && selectedPass.credits >= billableParticipants)
        return selectedPass.price;
    }

    return null;
  };

  const handleApplyCode = async () => {
    const rawPrice = getRawNumericPrice();
    if (!promoInput.trim() || rawPrice === null || !studio?.id) return;

    setIsApplyingDiscount(true);
    setDiscountError(null);

    try {
      const data = await api("/api/widget/promo/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: promoInput.trim(),
          amount: rawPrice,
          studioId: studio.id,
        }),
      });

      setDiscountInfo(data);
      setPromoInput("");
    } catch {
      setDiscountError(t("invalidPromoCode"));
      toast.error(t("invalidPromoCode"));
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleClearCode = () => {
    clearDiscount();
    setPromoInput("");
  };

  const validationError = getValidationError();
  /* const originalPrice = getPrice(); */

  return (
    <>
      <div className="col-span-1">
        <Card className="p-6 sticky top-6" shadow="sm">
          <h2 className="text-lg text-gray-500 font-semibold mb-4">
            {t("summary")}
          </h2>
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold">
                  {getLocalizedClassName()}
                </h3>
                <div className="flex items-center gap-2 text-gray-500 mt-1 mb-4">
                  <IconCalendar size={20} />
                  <span>
                    {formatDateWithLocale(
                      new Date(selectedEvent.startAt as Date),
                      "dd MMMM yyyy • HH:mm",
                      locale,
                    )}{" "}
                    -{" "}
                    {formatDateWithLocale(
                      new Date(selectedEvent.endAt as Date),
                      "HH:mm",
                      locale,
                    )}
                  </span>
                </div>
                <Button
                  variant="light"
                  className="text-primary text-sm p-0"
                  onPress={() => setIsCancelDialogOpen(true)}
                >
                  {t("cancellationConditions") || "Cancellation conditions"}
                </Button>

                {/* <Divider className="my-2" /> */}

                {/* <div className="flex justify-between items-center mt-4">
                  <div>
                    <div className="flex flex-row items-center gap-2">
                      <IconUsers size={18} />
                      {t("participants")}
                    </div>
                  </div>

                  <span className="font-medium text-right">
                    {getTotalParticipantsCount()}
                  </span>
                </div> */}

                {/* <div className="flex flex-col gap-3 mt-3 mb-3"> */}
                {/* Main user - always first */}
                {/* <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {user?.name}{" "}
                        <span className="text-sm text-gray-500">
                          ({t("you")})
                        </span>
                      </p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                  </div> */}

                {/* Guest participants */}
                {/* {participants
                    .filter((p) => p.email !== user?.email)
                    .map((participant, index) => (
                      <div
                        key={participant.id}
                        className="flex items-start bg-gray-100 p-3 gap-3 rounded-lg"
                      >
                        <div className="flex-1 flex-col items-start gap-3 space-y-2">
                          <Input
                            placeholder={`${t("guestName")} ${index + 1}`}
                            value={participant.name || ""}
                            onChange={(e) =>
                              setParticipants(
                                participants.map((p) =>
                                  p.id === participant.id
                                    ? { ...p, name: e.target.value }
                                    : p,
                                ),
                              )
                            }
                            size="md"
                            variant="bordered"
                            className="w-full"
                          />

                          <Tooltip
                            content={t("firstTimeGuestContent")}
                            placement="left"
                          >
                            <Checkbox
                              size="sm"
                              isSelected={participant.isFirstTime}
                              onValueChange={(checked) =>
                                setParticipants(
                                  participants.map((p) =>
                                    p.id === participant.id
                                      ? { ...p, isFirstTime: checked }
                                      : p,
                                  ),
                                )
                              }
                              className="ml-1"
                            >
                              <span className="text-sm">
                                {t("firstTimeGuest")}
                              </span>
                            </Checkbox>
                          </Tooltip>
                        </div>

                        <Button
                          size="md"
                          variant="light"
                          color="danger"
                          onPress={() => removeParticipant(participant)}
                          isIconOnly
                        >
                          <IconSquareX size={16} />
                        </Button>
                      </div>
                    ))} */}
                {/* </div> */}

                <Divider className="my-2" />

                <div className="flex justify-between items-center mt-2">
                  <div className="flex-1">{getSubscriptionLabel()}</div>
                  <span className="font-medium text-right">
                    {getPassPrice()}
                  </span>
                </div>
              </div>

              {/* Credit usage breakdown for existing passes */}
              {renderCreditUsage()}

              {validationError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-600 text-sm">{validationError}</p>
                </div>
              )}

              {/* Promo / gift card input — only shown when a payment is required */}
              {(getRawNumericPrice() ?? 0) > 0 && (
                <div className="space-y-2">
                  {discountInfo ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <div className="flex items-center gap-2 text-green-700">
                        <IconDiscount2 size={16} />
                        <span className="text-sm font-medium">
                          {discountInfo.code}
                        </span>
                        {discountInfo.description && (
                          <span className="text-xs text-green-600">
                            — {discountInfo.description}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-green-700">
                          -{parseFloat(discountInfo.discountAmount).toFixed(2)}{" "}
                          {studio?.currency || "CHF"}
                        </span>
                        <Button
                          size="sm"
                          variant="light"
                          color="danger"
                          isIconOnly
                          onPress={handleClearCode}
                        >
                          <IconSquareX size={14} />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("promoCode")}
                        value={promoInput}
                        onValueChange={setPromoInput}
                        size="sm"
                        variant="bordered"
                        className="flex-1"
                        onKeyDown={(e: React.KeyboardEvent) =>
                          e.key === "Enter" && handleApplyCode()
                        }
                        isInvalid={!!discountError}
                        errorMessage={discountError ?? undefined}
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        onPress={handleApplyCode}
                        isLoading={isApplyingDiscount}
                        isDisabled={!promoInput.trim()}
                      >
                        {t("apply")}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="font-medium">{t("overall")}</span>
                <div className="text-right">
                  {discountInfo && (
                    <div className="text-sm text-gray-400 line-through">
                      {getOverall()}
                    </div>
                  )}
                  <div className="font-medium text-xl">
                    {getOverallWithDiscount()}
                  </div>
                  {getPassSubtitle() && (
                    <div className="text-sm text-gray-500 mt-1">
                      {getPassSubtitle()}
                    </div>
                  )}
                </div>
              </div>

              {/* Show validation errors */}
              {!isBookingValid() && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-600 text-sm">{getOverall()}</p>
                </div>
              )}

              {bookingMethod !== BookingMethod.USER_PASS && (
                <div className="text-sm text-gray-500 text-right">
                  {t("inclVat")}
                </div>
              )}

              <PaymentButton
                pay={pay}
                isLoading={isLoading}
                isBookingValid={isBookingValidHook}
                buttonLabel={buttonLabel}
              />

              {/* <Button
                color="primary"
                size="lg"
                variant="ghost"
                onPress={handleAddGuest}
                startContent={<IconUserPlus size={18} />}
                className="mt-4 w-full"
              >
                {t("addGuest")}
              </Button> */}
            </div>
          )}
        </Card>
      </div>

      <CancelDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={() => setIsCancelDialogOpen(false)}
      />
    </>
  );
}

export default Summary;
